import { useState } from "react";
import { setEventsEnabled } from "@/lib/api/admin";
import { useAdminToggleMutation } from "@/lib/hooks/useAdminToggleMutation";
import type { Store } from "@/lib/api/types";
import { CalendarDays } from "lucide-react";
import { AdminOverrideCard } from "@/components/AdminOverrideCard";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type StoreEventsEnabledSectionProps = {
  store: Store;
};

export function StoreEventsEnabledSection({ store }: StoreEventsEnabledSectionProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const toggleMutation = useAdminToggleMutation<boolean>(
    store.id,
    setEventsEnabled,
    "Events enabled override updated.",
    () => setConfirmOpen(false)
  );

  if (store.category === "nightclub") {
    return null;
  }

  return (
    <>
      <AdminOverrideCard
        icon={CalendarDays}
        title="Event Creation"
        statusLabel={store.events_enabled ? "Override Active" : "Nightclub-only (default)"}
        statusDescription={
          store.events_enabled
            ? "This store can create and list events despite not being a nightclub."
            : "Only nightclub-category stores can create events unless granted here."
        }
        action={{
          label: store.events_enabled ? "Revoke Override" : "Grant Override",
          onClick: () => setConfirmOpen(true),
          disabled: toggleMutation.isPending,
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          store.events_enabled ? "Revoke event-creation override" : "Grant event-creation override"
        }
        description={
          store.events_enabled
            ? `Revoke the events_enabled override for "${store.name}"? Its Events tab and any existing events will be hidden until re-granted.`
            : `Grant "${store.name}" the ability to create and manage events, even though it isn't a nightclub?`
        }
        confirmLabel={store.events_enabled ? "Revoke" : "Grant"}
        destructive={store.events_enabled}
        isPending={toggleMutation.isPending}
        onConfirm={() => toggleMutation.mutate(!store.events_enabled)}
      />
    </>
  );
}
